
export interface PreviewContent {
  type: 'text' | 'image' | 'code' | 'canvas';
  content: string;
  name?: string;
  thumbnailUrl?: string; // Add this property for storing the first image URL
}

export type ContentType = 'text' | 'image' | 'code' | 'canvas' | 'file';

export function getChatContentType(messages: any[]): ContentType {
  if (messages.length === 0) {
    return 'text';
  }

  const latestMessage = messages[messages.length - 1];

  if (latestMessage.attachments && latestMessage.attachments.length > 0) {
    const attachmentTypes = latestMessage.attachments.map(att => att.type);

    if (attachmentTypes.includes('image')) {
      return 'image';
    } else if (attachmentTypes.includes('code')) {
      return 'code';
    } else if (attachmentTypes.includes('canvas')) {
      return 'canvas';
    } else {
      return 'file';
    }
  }

  return 'text';
}

export function countAttachmentTypes(messages: any[]): { imageCount: number; codeCount: number; canvasCount: number } {
  let imageCount = 0;
  let codeCount = 0;
  let canvasCount = 0;

  messages.forEach(message => {
    if (message.attachments) {
      message.attachments.forEach(attachment => {
        if (attachment.type === 'image') {
          imageCount++;
        } else if (attachment.type === 'code') {
          codeCount++;
        } else if (attachment.type === 'canvas') {
          canvasCount++;
        }
      });
    }
  });

  return { imageCount, codeCount, canvasCount };
}

// Find the first image attachment in all messages
function findFirstImageUrl(messages: any[]): string | undefined {
  for (const message of messages) {
    if (message.attachments) {
      const imageAttachment = message.attachments.find(att => att.type === 'image');
      if (imageAttachment) {
        return imageAttachment.content;
      }
    }
  }
  return undefined;
}

export function getPreviewContent(messages: any[]): PreviewContent {
  if (messages.length === 0) {
    return {
      type: 'text',
      content: 'No messages yet'
    };
  }

  // Get the most recent message with content
  let latestMessage = messages[messages.length - 1];
  
  // Find first image URL for thumbnail
  const thumbnailUrl = findFirstImageUrl(messages);
  
  // Look for attachments first
  if (latestMessage.attachments && latestMessage.attachments.length > 0) {
    const firstAttachment = latestMessage.attachments[0];
    
    // Image attachment has priority in preview
    if (firstAttachment.type === 'image') {
      return {
        type: 'image',
        content: firstAttachment.content,
        name: firstAttachment.name,
        thumbnailUrl
      };
    }
    
    // Canvas drawing
    if (firstAttachment.type === 'canvas') {
      return {
        type: 'canvas',
        content: firstAttachment.content,
        name: 'Drawing',
        thumbnailUrl
      };
    }
    
    // Code snippet
    if (firstAttachment.type === 'code') {
      return {
        type: 'code',
        content: firstAttachment.content,
        name: firstAttachment.name || 'Code snippet',
        thumbnailUrl
      };
    }
  }
  
  // If no attachments or not a type we can preview specially, use the message content
  return {
    type: 'text',
    content: latestMessage.content,
    thumbnailUrl
  };
}
